import React, { useState } from "react";
import { motion, useMotionValue, useTransform, AnimatePresence } from "framer-motion";
import { Check, X, RotateCcw } from "lucide-react";

/**
 * Tinder-style swipeable maintenance suggestion deck.
 * Right swipe = keep / add to schedule. Left swipe = skip.
 * Props:
 *   suggestions: [{ name, interval, unit, notes }]
 *   onFinish: (kept: Array<{...}>) => void
 */
export default function SwipeSuggestions({ suggestions, onFinish }) {
  const [index, setIndex] = useState(0);
  const [kept, setKept] = useState([]);
  const [decisions, setDecisions] = useState([]); // {idx, direction}
  const current = suggestions[index];
  const total = suggestions.length;
  const finished = index >= total;

  const swipe = (dir) => {
    const s = suggestions[index];
    const nextKept = dir === "right" ? [...kept, s] : kept;
    const nextDecisions = [...decisions, { idx: index, dir, item: s }];
    setKept(nextKept);
    setDecisions(nextDecisions);
    setIndex(index + 1);
  };

  const undo = () => {
    if (decisions.length === 0) return;
    const last = decisions[decisions.length - 1];
    setDecisions(decisions.slice(0, -1));
    setIndex(Math.max(0, index - 1));
    if (last.dir === "right") {
      setKept(kept.slice(0, -1));
    }
  };

  const finishNow = () => onFinish(kept);

  if (finished) {
    return (
      <div className="text-center py-10">
        <div className="h-16 w-16 mx-auto rounded-sm bg-orange-600 grid place-items-center mb-4">
          <Check className="w-8 h-8 text-white" strokeWidth={3} />
        </div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-orange-500 mb-2">// DECK CLEARED</div>
        <h3 className="font-display font-black text-3xl tracking-tight mb-2">
          {kept.length}/{total} tasks scheduled
        </h3>
        <p className="text-zinc-400 mb-6 text-sm">Swipe deck complete. You can always add more later.</p>
        <div className="flex gap-3 justify-center">
          <button
            onClick={undo}
            type="button"
            className="px-4 py-2 text-xs uppercase tracking-widest bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 rounded-sm"
            data-testid="undo-last"
          >
            <RotateCcw className="w-3 h-3 inline mr-1" /> Undo
          </button>
          <button
            onClick={finishNow}
            type="button"
            className="px-6 py-2 bg-orange-600 hover:bg-orange-500 text-white rounded-sm uppercase tracking-widest text-xs font-bold"
            data-testid="confirm-suggestions"
          >
            Save schedule →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
          Task {index + 1} / {total}
        </div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-orange-500">
          {kept.length} queued
        </div>
      </div>

      {/* Progress */}
      <div className="h-1 bg-zinc-900 rounded-sm mb-4 overflow-hidden">
        <div
          className="h-full bg-orange-600 transition-all duration-300"
          style={{ width: `${((index + 1) / total) * 100}%` }}
        />
      </div>

      {/* Direction hint banner — always visible above the deck */}
      <div className="grid grid-cols-2 gap-2 mb-3 text-[11px] sm:text-xs font-bold uppercase tracking-widest">
        <div className="bg-red-950/30 border border-red-900/50 rounded-sm px-3 py-2 text-red-400 flex items-center gap-2">
          <X className="w-4 h-4" strokeWidth={2.8} />
          <span>Left = skip</span>
        </div>
        <div className="bg-orange-950/30 border border-orange-600/50 rounded-sm px-3 py-2 text-orange-400 flex items-center justify-end gap-2">
          <span>Right = add</span>
          <Check className="w-4 h-4" strokeWidth={2.8} />
        </div>
      </div>

      <div className="relative h-64 swipe-card-container">
        <AnimatePresence>
          {[index + 1, index].map((i) => {
            if (i >= total || i < index) return null;
            const s = suggestions[i];
            const isTop = i === index;
            return (
              <SwipeCard
                key={i}
                suggestion={s}
                isTop={isTop}
                offset={i - index}
                onSwipe={isTop ? swipe : undefined}
              />
            );
          })}
        </AnimatePresence>
      </div>

      <div className="flex items-center justify-center gap-4 mt-8">
        <button
          onClick={() => swipe("left")}
          type="button"
          className="h-16 w-16 rounded-sm border-2 border-red-500 hover:bg-red-500 text-red-400 hover:text-white grid place-items-center transition-all group"
          data-testid="swipe-skip"
          aria-label="Skip this task"
        >
          <div className="flex flex-col items-center">
            <X className="w-6 h-6" strokeWidth={3} />
            <span className="text-[9px] uppercase tracking-widest font-bold mt-0.5">Skip</span>
          </div>
        </button>
        <button
          onClick={undo}
          type="button"
          disabled={decisions.length === 0}
          className="h-10 w-10 rounded-sm border border-zinc-800 text-zinc-500 hover:text-zinc-200 disabled:opacity-30 grid place-items-center"
          data-testid="swipe-undo"
          aria-label="Undo"
          title="Undo"
        >
          <RotateCcw className="w-4 h-4" />
        </button>
        <button
          onClick={() => swipe("right")}
          type="button"
          className="h-16 w-16 rounded-sm border-2 border-orange-600 bg-orange-600/10 hover:bg-orange-600 text-orange-400 hover:text-white grid place-items-center transition-all group"
          data-testid="swipe-keep"
          aria-label="Add this task to schedule"
        >
          <div className="flex flex-col items-center">
            <Check className="w-6 h-6" strokeWidth={3} />
            <span className="text-[9px] uppercase tracking-widest font-bold mt-0.5">Add</span>
          </div>
        </button>
      </div>

      <div className="text-center text-[10px] uppercase tracking-[0.3em] text-zinc-600 mt-3">
        Drag card · tap a button · arrow keys ← →
      </div>

      <div className="flex justify-end mt-6">
        <button
          onClick={finishNow}
          type="button"
          className="text-xs uppercase tracking-widest text-zinc-500 hover:text-orange-500"
          data-testid="finish-early"
        >
          Skip rest & save →
        </button>
      </div>
    </div>
  );
}

function SwipeCard({ suggestion, isTop, offset, onSwipe }) {
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-15, 15]);
  const keepOpacity = useTransform(x, [0, 120], [0, 1]);
  const skipOpacity = useTransform(x, [-120, 0], [1, 0]);

  const handleDragEnd = (_, info) => {
    const threshold = 100;
    if (info.offset.x > threshold) onSwipe?.("right");
    else if (info.offset.x < -threshold) onSwipe?.("left");
  };

  return (
    <motion.div
      drag={isTop ? "x" : false}
      dragConstraints={{ left: 0, right: 0 }}
      onDragEnd={handleDragEnd}
      style={isTop ? { x, rotate } : {}}
      initial={{ scale: 1 - offset * 0.05, y: offset * 8, opacity: isTop ? 1 : 0.5 }}
      animate={{ scale: 1 - offset * 0.05, y: offset * 8, opacity: isTop ? 1 : 0.5 }}
      exit={{ x: x.get() > 0 ? 400 : -400, opacity: 0, transition: { duration: 0.25 } }}
      className={`absolute inset-0 rounded-sm bg-zinc-900 border border-zinc-800 p-6 ${isTop ? "cursor-grab active:cursor-grabbing" : "pointer-events-none"}`}
      data-testid={isTop ? "swipe-top-card" : "swipe-under-card"}
    >
      {isTop && (
        <>
          <motion.div
            style={{ opacity: keepOpacity }}
            className="absolute top-4 right-4 font-display font-black text-2xl tracking-wider text-orange-500 border-4 border-orange-500 rounded-sm px-3 py-1 rotate-12"
          >
            ADD
          </motion.div>
          <motion.div
            style={{ opacity: skipOpacity }}
            className="absolute top-4 left-4 font-display font-black text-2xl tracking-wider text-red-500 border-4 border-red-500 rounded-sm px-3 py-1 -rotate-12"
          >
            SKIP
          </motion.div>
        </>
      )}

      <div className="h-full flex flex-col">
        <div className="text-[10px] uppercase tracking-[0.3em] text-orange-500 mb-3">
          // Suggested task
        </div>
        <h4 className="font-display font-black text-2xl sm:text-3xl tracking-tight mb-3">
          {suggestion.name}
        </h4>
        <div className="font-mono text-sm text-zinc-400 mb-3">
          Every {suggestion.interval} {suggestion.unit}
        </div>
        {suggestion.notes ? (
          <p className="text-zinc-400 text-sm">{suggestion.notes}</p>
        ) : null}
        <div className="mt-auto pt-4 border-t border-zinc-800">
          <div className="flex items-center justify-between text-[10px] uppercase tracking-widest font-bold">
            <span className="text-red-400 flex items-center gap-1"><X className="w-3 h-3" strokeWidth={3} /> Swipe left to skip</span>
            <span className="text-orange-500 flex items-center gap-1">Swipe right to add <Check className="w-3 h-3" strokeWidth={3} /></span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
